import type { ReactNode } from 'react'
import { Construction } from 'lucide-react'
import { useTranslation } from '@/i18n'
import AppSidebar from '@/components/AppSidebar'
import { IntuiEmptyState } from '@/components/intueri/IntuiEmptyState'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import { PAGE_HEADER, PAGE_TITLE } from '@/components/layout/pageHeader'

/** The page shell (sidebar and header) around whatever the gate shows before a workflow page can. */
function WorkflowShell({ children }: { children?: ReactNode }) {
  const { t } = useTranslation()
  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        <header className={PAGE_HEADER}>
          <span className={PAGE_TITLE}>
            {t('nav:menu.workflow', { defaultValue: 'Workflow' })}
          </span>
        </header>
        <div className="flex flex-1 items-center justify-center p-6">{children}</div>
      </div>
    </div>
  )
}

function WorkflowUnderConstruction() {
  const { t } = useTranslation()
  return (
    <WorkflowShell>
      <IntuiEmptyState
        icon={<Construction className="size-4 text-muted-foreground" />}
        title={t('workflow:under_construction.title', {
          defaultValue: 'Under heavy construction',
        })}
        description={t('workflow:under_construction.description', {
          defaultValue: 'They will be back here soon.',
        })}
        className="w-[308px]"
      />
    </WorkflowShell>
  )
}

/**
 * Which workflow page a user sees: the legacy shell, then runs on the agent harness (`harness`, when
 * `workflow_harness_enabled` is on and the route offers one), then the engine's v4 page, else the construction screen.
 */
export function WorkflowPageGate({ children, v4, harness }: { children: ReactNode; v4?: ReactNode; harness?: ReactNode }) {
  const legacy = useApiFeatureFlag('workflow_legacy_page_enabled')
  const harnessFlag = useApiFeatureFlag('workflow_harness_enabled')
  const v4Flag = useApiFeatureFlag('workflow_v4_page_enabled')
  if (legacy.enabled) return <>{children}</>
  if (harness && harnessFlag.enabled) return <>{harness}</>
  if (v4Flag.enabled) return <>{v4 ?? children}</>
  // Until the flags have answered, the empty shell: the construction screen flashed on every cold
  // load before the flags arrived, even for an organization whose workflows are on (Pegah, 2026-10-31).
  if (legacy.loading || harnessFlag.loading || v4Flag.loading) return <WorkflowShell />
  return <WorkflowUnderConstruction />
}
